/**
 * Date and time rendering that is safe to run on both the server and the client.
 *
 * The bug this exists to prevent: `toLocaleDateString` / `toLocaleTimeString` /
 * `Intl.DateTimeFormat` with no `timeZone` option resolve against *the ambient
 * zone of whoever is running them*. During SSR that is the server (UTC in this
 * project); on hydration it is the user's device. The two disagree, the text
 * node differs, and React throws #418 and discards the server HTML.
 *
 * Two rules keep that from happening, and they are not the same rule:
 *
 * - **Locale governs the format** — `14:30` vs `2:30 pm`, month and weekday
 *   names. This follows the request locale.
 * - **Timezone governs the instant** — which moment in time the text refers to.
 *   This follows the *device*, never the locale. `en-US` is a formatting
 *   convention, not a location: a user in Berlin may well be on `en-US`, and
 *   inferring `America/New_York` from that would simply be wrong.
 *
 * Which helper to use is decided by the column type, not by preference:
 *
 * - `@db.Date` columns (e.g. `DiaryEntry.date`) are *zoneless calendar dates*.
 *   Prisma hands them back as UTC midnight and `actions/diary.ts` writes and
 *   queries them in UTC terms (`toISOString().slice(0, 10)`). Rendering one in a
 *   negative-offset zone shifts it to the previous day. Use
 *   {@link formatCalendarDate}, which pins UTC.
 * - `@db.Timestamptz` columns (e.g. `DiaryEntry.loggedAt`) are *real instants*
 *   and genuinely mean different wall-clock times to different viewers. Use
 *   {@link formatInstant} with the viewer's zone.
 */
import { logger } from '@lib/logger'

/** Zoneless calendar dates are always rendered here. See the module comment. */
const CALENDAR_TIME_ZONE = 'UTC'

/**
 * Whether a string is an IANA time zone this runtime can actually format in.
 *
 * Implemented by asking `Intl` to build a formatter, which throws `RangeError`
 * for anything it does not recognise. Preferred over `Intl.supportedValuesOf`
 * because it works on older runtimes and still accepts valid legacy aliases
 * (`Asia/Calcutta`) that the enumerated list omits.
 *
 * This is the validation gate for anything client-supplied — see
 * `actions/timezone.ts`.
 */
export function isValidTimeZone(timeZone: string): boolean {
  // Cheap guards first so a hostile client cannot push a huge string into Intl.
  if (!timeZone || timeZone.length > 64) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return true
  } catch (err) {
    logger.error('timezone-cookie', 'invalid IANA time zone', err, { timeZone })
    return false
  }
}

/** Falls back to UTC rather than throwing, so a bad cookie can never blank a page. */
function safeTimeZone(timeZone: string | null | undefined): string {
  if (!timeZone) return CALENDAR_TIME_ZONE
  return isValidTimeZone(timeZone) ? timeZone : CALENDAR_TIME_ZONE
}

/**
 * Render a zoneless calendar date (`@db.Date`).
 *
 * Always formatted in UTC, which is both what makes it deterministic across
 * server and client and what matches how the column is written. Do not add a
 * `timeZone` override — shifting a calendar date by a zone offset is the bug,
 * not the fix.
 */
export function formatCalendarDate(
  date: Date | string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' },
): string {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: CALENDAR_TIME_ZONE,
  }).format(new Date(date))
}

/**
 * Render a real instant (`@db.Timestamptz`) in an explicit zone.
 *
 * `timeZone` comes from the device via the `tz` cookie (see
 * `lib/timezone-cookie.ts`), never from the locale. When it is absent — the
 * user's first visit, before the cookie is written — callers should render
 * `components/ui/LocalTime.tsx` instead, which formats after mount rather than
 * guessing a zone the server does not know.
 */
export function formatInstant(
  date: Date | string,
  locale: string,
  timeZone: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' },
): string {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: safeTimeZone(timeZone),
  }).format(new Date(date))
}

/**
 * The `YYYY-MM-DD` calendar day an instant falls on, in a given zone.
 *
 * Used for "is this cell today" comparisons, which must be computed once on the
 * server and passed down — computing it independently on both sides of a
 * hydration boundary is what makes the calendar's today-highlight flicker and
 * mismatch near midnight.
 *
 * Built from `formatToParts` rather than a locale that happens to emit ISO
 * order, so the shape does not depend on locale data.
 */
export function calendarDayKey(date: Date, timeZone: string | null | undefined): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: safeTimeZone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)

  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''

  return `${part('year')}-${part('month')}-${part('day')}`
}

/** UTC midnight for a zoneless `YYYY-MM-DD` calendar day (`@db.Date`). */
export function utcMidnightFromDayKey(dayKey: string): Date {
  return new Date(`${dayKey}T00:00:00.000Z`)
}

/** Shift a `YYYY-MM-DD` key by whole UTC calendar days. */
export function shiftDayKey(dayKey: string, delta: number): string {
  const d = utcMidnightFromDayKey(dayKey)
  d.setUTCDate(d.getUTCDate() + delta)
  return d.toISOString().slice(0, 10)
}

/** Half-open UTC range `[start, end)` covering a 0-indexed calendar month. */
export function utcMonthRange(year: number, monthIndex: number): { start: Date; end: Date } {
  return {
    start: new Date(Date.UTC(year, monthIndex, 1)),
    end: new Date(Date.UTC(year, monthIndex + 1, 1)),
  }
}
